import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getWeightHistory, recordWeight, deleteWeightEntry } from '../firebase/measurements';
import { formatDate } from '../utils/membershipFees';
import {
  CHART,
  WEIGHT_UNITS,
  buildChartModel,
  buildWeightSeries,
  formatChange,
  formatWeight,
  summarizeWeightProgress
} from '../utils/weightTracking';
import './WeightTracker.css';

function WeightTracker({ tenantId, member, onRecorded }) {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState('');
  const [hoverIndex, setHoverIndex] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [form, setForm] = useState({
    weight: '',
    unit: WEIGHT_UNITS.KG,
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const loadHistory = useCallback(async () => {
    if (!tenantId || !member?.id) return;
    setIsLoading(true);
    const result = await getWeightHistory(tenantId, member.id);
    if (result.success) {
      setEntries(result.entries);
      setError(null);
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  }, [tenantId, member?.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Registration weight seeds the series when nothing has been recorded yet
  const series = useMemo(() => buildWeightSeries(member, entries), [member, entries]);
  const progress = useMemo(() => summarizeWeightProgress(series), [series]);
  const displayUnit = progress.latest?.unit || WEIGHT_UNITS.KG;
  const chart = useMemo(() => buildChartModel(series), [series]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError(null);

    const result = await recordWeight(tenantId, member.id, form);

    if (result.success) {
      setForm({
        weight: '',
        unit: form.unit,
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setShowForm(false);
      setNotice('Weight recorded.');
      setTimeout(() => setNotice(''), 4000);
      await loadHistory();
      onRecorded && onRecorded();
    } else {
      setError(result.message);
    }

    setIsSaving(false);
  };

  const handleDelete = async (entry) => {
    if (entry.synthetic) return;
    const result = await deleteWeightEntry(tenantId, member.id, entry.id);
    if (result.success) {
      await loadHistory();
      onRecorded && onRecorded();
    } else {
      setError(result.message);
    }
  };

  const active = hoverIndex !== null && chart ? chart.points[hoverIndex] : null;

  return (
    <div className="weight-tracker">
      <div className="weight-tracker-header">
        <h3>⚖️ Weight Progress</h3>
        <button type="button" className="weight-record-btn" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '＋ Record Weight'}
        </button>
      </div>

      {notice && <div className="weight-notice success">✅ {notice}</div>}
      {error && <div className="weight-notice error">{error}</div>}

      {showForm && (
        <form className="weight-form" onSubmit={handleSubmit}>
          <div className="weight-form-row">
            <div className="weight-field">
              <label htmlFor="weightValue">Weight</label>
              <input
                id="weightValue"
                type="number"
                step="0.1"
                min="1"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
                placeholder="e.g. 72.5"
                required
              />
            </div>
            <div className="weight-field">
              <label htmlFor="weightUnit">Unit</label>
              <select
                id="weightUnit"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              >
                <option value={WEIGHT_UNITS.KG}>kg</option>
                <option value={WEIGHT_UNITS.LB}>lb</option>
              </select>
            </div>
            <div className="weight-field">
              <label htmlFor="weightDate">Date</label>
              <input
                id="weightDate"
                type="date"
                value={form.date}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="weight-field">
            <label htmlFor="weightNotes">Notes (optional)</label>
            <input
              id="weightNotes"
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. after 6-week program"
            />
          </div>
          <button type="submit" className="weight-submit-btn" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Reading'}
          </button>
        </form>
      )}

      {isLoading ? (
        <p className="weight-empty">Loading weight history...</p>
      ) : !progress.hasData ? (
        <p className="weight-empty">
          No weight recorded yet. Add a reading to start tracking progress over time.
        </p>
      ) : (
        <>
          {/* Stat tiles: current value + change, per the form heuristic */}
          <div className="weight-stats">
            <div className="weight-stat">
              <span className="weight-stat-label">Current</span>
              <span className="weight-stat-value">
                {formatWeight(progress.latest.weightKg, displayUnit)}
              </span>
              <span className="weight-stat-meta">{formatDate(progress.latest.measuredAt)}</span>
            </div>
            <div className="weight-stat">
              <span className="weight-stat-label">Since Start</span>
              <span className={`weight-stat-value change-${progress.direction}`}>
                {formatChange(progress.change, displayUnit)}
              </span>
              <span className="weight-stat-meta">
                {progress.changePercent === 0
                  ? 'No change'
                  : `${progress.changePercent > 0 ? '+' : ''}${progress.changePercent}%`}
              </span>
            </div>
            <div className="weight-stat">
              <span className="weight-stat-label">Starting</span>
              <span className="weight-stat-value">
                {formatWeight(progress.baseline.weightKg, displayUnit)}
              </span>
              <span className="weight-stat-meta">{formatDate(progress.baseline.measuredAt)}</span>
            </div>
            <div className="weight-stat">
              <span className="weight-stat-label">Readings</span>
              <span className="weight-stat-value">{progress.count}</span>
              <span className="weight-stat-meta">
                {progress.changeSincePrevious === null
                  ? 'First reading'
                  : `${formatChange(progress.changeSincePrevious, displayUnit)} vs last`}
              </span>
            </div>
          </div>

          {series.length === 1 ? (
            <p className="weight-empty">
              One reading so far — record another to see the trend.
            </p>
          ) : (
            <div className="weight-chart-wrap">
              <svg
                className="weight-chart"
                viewBox={`0 0 ${CHART.width} ${CHART.height}`}
                role="img"
                aria-label={`Weight over time, from ${formatWeight(progress.baseline.weightKg, displayUnit)} to ${formatWeight(progress.latest.weightKg, displayUnit)}`}
                onMouseLeave={() => setHoverIndex(null)}
                onMouseMove={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  const x = ((event.clientX - rect.left) / rect.width) * CHART.width;
                  let nearest = 0;
                  let best = Infinity;
                  chart.points.forEach((point, i) => {
                    const distance = Math.abs(point.x - x);
                    if (distance < best) {
                      best = distance;
                      nearest = i;
                    }
                  });
                  setHoverIndex(nearest);
                }}
              >
                {/* Recessive gridlines + axis labels */}
                {chart.ticks.map((tick) => (
                  <g key={tick.y}>
                    <line
                      x1={CHART.padLeft}
                      x2={CHART.width - CHART.padRight}
                      y1={tick.y}
                      y2={tick.y}
                      className="weight-grid"
                    />
                    <text x={CHART.padLeft - 8} y={tick.y + 4} className="weight-axis-label" textAnchor="end">
                      {Math.round(tick.value * 10) / 10}
                    </text>
                  </g>
                ))}

                {/* Area wash under the line */}
                <path
                  className="weight-area"
                  d={
                    `M ${chart.points[0].x} ${chart.baselineY} ` +
                    chart.points.map((p) => `L ${p.x} ${p.y}`).join(' ') +
                    ` L ${chart.points[chart.points.length - 1].x} ${chart.baselineY} Z`
                  }
                />

                <path
                  className="weight-line"
                  d={chart.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}
                />

                {/* Crosshair for the hovered reading */}
                {active && (
                  <line
                    x1={active.x}
                    x2={active.x}
                    y1={CHART.padTop}
                    y2={CHART.height - CHART.padBottom}
                    className="weight-crosshair"
                  />
                )}

                {chart.points.map((point) => (
                  <circle
                    key={point.id}
                    cx={point.x}
                    cy={point.y}
                    r={hoverIndex === point.index ? 6 : 4}
                    className="weight-dot"
                  />
                ))}

                {/* Label the endpoint only - the starting value is in the stat tiles,
                    and a label at the first point collides with the y-axis ticks.
                    Hidden while hovered, since the tooltip already carries the value. */}
                {hoverIndex !== chart.points.length - 1 && (
                  <text
                    x={chart.points[chart.points.length - 1].x}
                    y={chart.points[chart.points.length - 1].y - 12}
                    className="weight-point-label"
                    textAnchor="end"
                  >
                    {Math.round(chart.points[chart.points.length - 1].weightKg * 10) / 10}
                  </text>
                )}

                <text
                  x={CHART.padLeft}
                  y={CHART.height - 8}
                  className="weight-axis-label"
                  textAnchor="start"
                >
                  {formatDate(series[0].measuredAt)}
                </text>
                <text
                  x={CHART.width - CHART.padRight}
                  y={CHART.height - 8}
                  className="weight-axis-label"
                  textAnchor="end"
                >
                  {formatDate(series[series.length - 1].measuredAt)}
                </text>
              </svg>

              {active && (
                <div
                  className="weight-tooltip"
                  style={{
                    // Clamped so the tooltip never spills past the chart edges
                    left: `${Math.min(88, Math.max(12, (active.x / CHART.width) * 100))}%`,
                    top: `${(active.y / CHART.height) * 100}%`
                  }}
                >
                  <strong>{formatWeight(active.weightKg, displayUnit)}</strong>
                  <span>{formatDate(active.measuredAt)}</span>
                  {active.notes && <span className="weight-tooltip-note">{active.notes}</span>}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="weight-table-toggle"
            onClick={() => setShowTable((v) => !v)}
          >
            {showTable ? 'Hide all readings' : `View all ${progress.count} readings`}
          </button>

          {/* Table view - every value stays available, never gated behind the chart */}
          {showTable && (
            <div className="payment-history-table weight-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Weight</th>
                    <th>Change</th>
                    <th>Notes</th>
                    <th>Recorded By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...series].reverse().map((entry, index, reversed) => {
                    const older = reversed[index + 1];
                    const delta = older ? entry.weightKg - older.weightKg : null;

                    return (
                      <tr key={entry.id}>
                        <td>{formatDate(entry.measuredAt)}</td>
                        <td>{formatWeight(entry.weightKg, displayUnit)}</td>
                        <td className={delta === null ? '' : delta < 0 ? 'change-down' : delta > 0 ? 'change-up' : ''}>
                          {delta === null ? '—' : formatChange(delta, displayUnit)}
                        </td>
                        <td>{entry.notes || (entry.synthetic ? 'From registration form' : '—')}</td>
                        <td>{entry.recordedBy || '—'}</td>
                        <td>
                          {!entry.synthetic && (
                            <button
                              type="button"
                              className="weight-delete-btn"
                              title="Delete this reading"
                              onClick={() => handleDelete(entry)}
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WeightTracker;
